import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import {
  getActiveAIModelSetups,
  generateAIContent,
} from "../../services/aiGenerateService";
import { handleErrors } from "../../utils/errorHandler";
import { getModelSetupLabel, getSetupId, findDefaultSetupId } from "./AIUtils";
import { parseAIResponse, getAIResponseData } from "./AIResponseParser";
import { buildInjectionPlan, injectAIFields } from "./AIFieldInjector";
import "./ai-assistant.css";
import "../Common/Loaders/loaders.css";

const GENERATION_STEPS = [
  "Analyzing your request...",
  "Building the content structure...",
  "Writing the article...",
  "Optimizing SEO details...",
  "Finalizing the draft...",
];

const GENERATION_STATUS = {
  IDLE: "idle",
  SUCCESS: "success",
  ERROR: "error",
};

const PROMPT_MAX_LENGTH = 2000;
const PROMPT_MIN_LENGTH = 10;
const MODEL_STORAGE_KEY = "xcruit_ai_selected_model";

const PROMPT_EXAMPLES = [
 "Write a detailed article about Artificial Intelligence in the Global Energy Industry", "Generate SEO content for Jobs in Oil, Gas, and Renewable Energy Sectors", "Create a travel article about Worldwide Energy Innovation Events and Sustainability Conferences"
];

const AiAssistantOffcanvas = ({
  pageLink,
  formData,
  setFormData,
  fieldMap = {},
  editorFields = [],
  editorHandlers = {},
  onAfterInject,
  buildDynamicFields,
  title = "AI Assistant",
}) => {
  const safePageLink = (pageLink || "").trim().replace(/^\//, "");
  const [isOpen, setIsOpen] = useState(false);
  const [models, setModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [selectedModelId, setSelectedModelId] = useState("");
  const [userPrompt, setUserPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState(
    GENERATION_STATUS.IDLE
  );
  const [generationStep, setGenerationStep] = useState(0);

  const promptRef = useRef(null);
  const requestInFlightRef = useRef(false);

  const promptLength = userPrompt.length;
  const trimmedPromptLength = userPrompt.trim().length;

  const canGenerate =
    Boolean(selectedModelId) &&
    trimmedPromptLength >= PROMPT_MIN_LENGTH &&
    !generating;

  const showEmptyHelper =
    !generating &&
    generationStatus !== GENERATION_STATUS.SUCCESS &&
    generationStatus !== GENERATION_STATUS.ERROR &&
    !canGenerate;

  const resolveInitialModelId = useCallback((list) => {
    const saved = localStorage.getItem(MODEL_STORAGE_KEY);
    if (saved && list.some((m) => String(getSetupId(m)) === saved)) {
      return saved;
    }
    return findDefaultSetupId(list) || "";
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadModels = async () => {
      setModelsLoading(true);
      try {
        const list = await getActiveAIModelSetups();
        if (cancelled) return;
        const safeList = Array.isArray(list) ? list : [];
        setModels(safeList);
        setSelectedModelId((prev) => prev || resolveInitialModelId(safeList));
      } catch (error) {
        if (!cancelled) handleErrors(error);
      } finally {
        if (!cancelled) setModelsLoading(false);
      }
    };
    loadModels();
    return () => {
      cancelled = true;
    };
  }, [resolveInitialModelId]);

  useEffect(() => {
    if (selectedModelId) {
      localStorage.setItem(MODEL_STORAGE_KEY, selectedModelId);
    }
  }, [selectedModelId]);

  useEffect(() => {
    if (!generating) {
      setGenerationStep(0);
      return undefined;
    }
    const interval = window.setInterval(() => {
      setGenerationStep((prev) =>
        prev + 1 < GENERATION_STEPS.length ? prev + 1 : prev
      );
    }, 1800);
    return () => window.clearInterval(interval);
  }, [generating]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const timer = window.setTimeout(() => {
      promptRef.current?.focus();
    }, 280);

    return () => window.clearTimeout(timer);
  }, [isOpen]);

  const openOffcanvas = () => {
    setIsOpen(true);
  };

  const closeOffcanvas = () => {
    if (generating) return;
    setIsOpen(false);
  };

  const handlePromptChange = (e) => {
    const next = e.target.value;
    if (next.length <= PROMPT_MAX_LENGTH) {
      setUserPrompt(next);
      if (generationStatus === GENERATION_STATUS.ERROR) {
        setGenerationStatus(GENERATION_STATUS.IDLE);
      }
    }
  };

  const runGeneration = useCallback(async () => {
    if (requestInFlightRef.current || generating) return;

    if (!selectedModelId) {
      toast.warning("Please select an AI model.");
      return;
    }
    if (trimmedPromptLength < PROMPT_MIN_LENGTH) {
      toast.warning("Please enter at least 10 characters in the prompt.");
      return;
    }
    if (!safePageLink) {
      toast.error("Page link is missing for AI generation.");
      return;
    }

    requestInFlightRef.current = true;
    setGenerating(true);
    setGenerationStatus(GENERATION_STATUS.IDLE);

    try {
      const promptText = userPrompt.trim();
      const dynamicFields =
        typeof buildDynamicFields === "function"
          ? buildDynamicFields(promptText, formData)
          : { UserPrompt: promptText };

      const payload = {
        pageLink: safePageLink,
        tokenSetupId: Number(selectedModelId),
        userPrompt: promptText,
        dynamicFields,
      };

      const response = await generateAIContent(payload);

      if (response?.data?.isSuccess === false) {
        setGenerationStatus(GENERATION_STATUS.ERROR);
        toast.error("Unable to generate AI content right now. Please try again.");
        return;
      }

      const aiResponseData = getAIResponseData(response);
      if (!aiResponseData) {
        setGenerationStatus(GENERATION_STATUS.ERROR);
        toast.error(
          "AI generated an invalid response. Please regenerate."
        );
        return;
      }

      const { success, data, error } = parseAIResponse(aiResponseData);
      if (!success || !data) {
        setGenerationStatus(GENERATION_STATUS.ERROR);
        toast.error(
          error
            ? "AI generated an invalid response. Please regenerate."
            : "Unable to generate AI content right now. Please try again."
        );
        return;
      }

      const plan = buildInjectionPlan(data, { fieldMap, editorFields });
      if (!plan.length) {
        setGenerationStatus(GENERATION_STATUS.ERROR);
        toast.error(
          "AI generated an invalid response. Please regenerate."
        );
        return;
      }

      injectAIFields({ setFormData, plan, editorHandlers });

      setGenerationStatus(GENERATION_STATUS.SUCCESS);
      toast.success("AI content generated successfully.");

      if (onAfterInject) onAfterInject(formData, plan.map((p) => p.key), data);
    } catch {
      setGenerationStatus(GENERATION_STATUS.ERROR);
      toast.error("Unable to generate AI content right now. Please try again.");
    } finally {
      setGenerating(false);
      requestInFlightRef.current = false;
    }
  }, [
    generating,
    selectedModelId,
    trimmedPromptLength,
    userPrompt,
    safePageLink,
    fieldMap,
    editorFields,
    editorHandlers,
    setFormData,
    onAfterInject,
    formData,
    buildDynamicFields,
  ]);

  const renderStatusCard = () => {
    if (generating) {
      return (
        <div
          className="ai-assistant-state-card border rounded p-3 bg-light"
          aria-live="polite"
        >
          <div className="xc-ai-generating">
            <span className="xc-ai-generating__badge">
              <i className="ri-sparkling-2-line" aria-hidden="true" />
            </span>
            <div>
              <p className="xc-ai-generating__title">
                Generating content
                <span className="xc-ai-dots">
                  <span />
                  <span />
                  <span />
                </span>
              </p>
              <p className="xc-ai-generating__step" key={generationStep}>
                {GENERATION_STEPS[generationStep]}
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (generationStatus === GENERATION_STATUS.SUCCESS) {
      return (
        <div className="ai-assistant-state-card border border-success-subtle rounded p-3 bg-success-subtle">
          <div className="ai-assistant-status text-success">
            <i className="ri-checkbox-circle-line me-2" aria-hidden="true" />
            AI content generated successfully.
          </div>
        </div>
      );
    }

    if (generationStatus === GENERATION_STATUS.ERROR) {
      return (
        <div
          className="ai-assistant-state-card border rounded p-3 bg-warning-subtle"
          role="alert"
        >
          <div className="ai-assistant-status text-body">
            <i className="ri-information-line me-2 text-warning" aria-hidden="true" />
            Unable to generate AI content right now. Please try again.
          </div>
        </div>
      );
    }

    if (showEmptyHelper) {
      return (
        <div className="ai-assistant-state-card border rounded p-3 bg-light">
          <div className="ai-assistant-status text-muted">
            Select an AI model and describe what content you want to generate.
          </div>
        </div>
      );
    }

    return null;
  };

  const getPrimaryActionLabel = () => {
    if (generating) {
      return { loading: true, text: "Generating..." };
    }
    if (generationStatus === GENERATION_STATUS.SUCCESS) {
      return { text: "Regenerate Content", icon: "ri-refresh-line" };
    }
    if (generationStatus === GENERATION_STATUS.ERROR) {
      return { text: "Generate Again", icon: "ri-refresh-line" };
    }
    return { text: "Generate Content", icon: "ri-sparkling-2-line" };
  };

  const primaryAction = getPrimaryActionLabel();
  const statusCard = renderStatusCard();

  const ui = (
    <>
      {!isOpen ? (
        <button
          type="button"
          className="btn btn-primary rounded-pill shadow ai-assistant-fab"
          onClick={openOffcanvas}
          title={title}
          aria-expanded={false}
          aria-controls="aiAssistantOffcanvas"
        >
          <i className="ri-magic-line me-1" aria-hidden="true" />
          AI Assistant
        </button>
      ) : null}

      {isOpen ? (
        <div
          className="offcanvas-backdrop fade show ai-assistant-backdrop"
          onClick={generating ? undefined : closeOffcanvas}
          aria-hidden="true"
        />
      ) : null}

      <div
        className={`offcanvas offcanvas-end ai-assistant-offcanvas d-flex flex-column ${
          isOpen ? "show" : ""
        }`}
        tabIndex="-1"
        id="aiAssistantOffcanvas"
        aria-labelledby="aiAssistantOffcanvasLabel"
        aria-modal={isOpen ? "true" : undefined}
        role="dialog"
        style={{ visibility: isOpen ? "visible" : "hidden" }}
      >
        <div className="offcanvas-header border-bottom pb-3">
          <div className="pe-3">
            <h5 className="offcanvas-title mb-1" id="aiAssistantOffcanvasLabel">
              <i className="ri-magic-line me-2 text-primary" aria-hidden="true" />
              {title}
            </h5>
            <p className="text-muted small mb-0">
              Generate article content using AI
            </p>
          </div>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={closeOffcanvas}
            disabled={generating}
          />
        </div>

        <div className="offcanvas-body ai-assistant-body d-flex flex-column p-4 pb-0">
          <div className="ai-assistant-field">
            <label htmlFor="ai-assistant-model" className="form-label">
              AI Model
            </label>
            <select
              id="ai-assistant-model"
              className="form-select"
              value={selectedModelId}
              onChange={(e) => setSelectedModelId(e.target.value)}
              disabled={modelsLoading || generating}
            >
              <option value="">
                {modelsLoading ? "Loading models…" : "Select model"}
              </option>
              {models.map((m) => (
                <option key={getSetupId(m)} value={String(getSetupId(m))}>
                  {getModelSetupLabel(m)}
                </option>
              ))}
            </select>
          </div>

          <div className="ai-assistant-field flex-grow-1">
            <label htmlFor="ai-assistant-prompt" className="form-label">
              Your prompt
            </label>
            <textarea
              ref={promptRef}
              id="ai-assistant-prompt"
              rows={5}
              className="form-control ai-assistant-textarea"
              placeholder="Describe the article content you want AI to generate..."
              value={userPrompt}
              onChange={handlePromptChange}
              disabled={generating}
              maxLength={PROMPT_MAX_LENGTH}
            />
            <div className="d-flex justify-content-between align-items-start gap-2 ai-assistant-helper-row">
              <p className="ai-assistant-helper mb-0 flex-grow-1">
                Prompt Master rules are automatically applied.
              </p>
              <span
                className={`ai-assistant-helper ai-assistant-char-count mb-0 flex-shrink-0 ${
                  promptLength >= PROMPT_MAX_LENGTH ? "text-warning" : ""
                }`}
              >
                {promptLength} / {PROMPT_MAX_LENGTH}
              </span>
            </div>
            <ul className="ai-assistant-examples list-unstyled mb-0 ps-0">
              {PROMPT_EXAMPLES.map((example) => (
                <li key={example}>
                  <i className="ri-arrow-right-s-line me-1" aria-hidden="true" />
                  {example}
                </li>
              ))}
            </ul>
          </div>

          {statusCard ? (
            <div className="ai-assistant-field ai-assistant-field--last">
              {statusCard}
            </div>
          ) : null}
        </div>

        <div className="offcanvas-footer ai-assistant-footer">
          <button
            type="button"
            className="btn btn-outline-secondary rounded-pill fw-semibold px-4 py-2 ai-assistant-footer__close"
            onClick={closeOffcanvas}
            disabled={generating}
          >
            Close Assistant
          </button>
          <button
            type="button"
            className="btn btn-primary rounded-pill fw-semibold px-4 py-2 ai-assistant-footer__primary"
            onClick={runGeneration}
            disabled={!canGenerate}
          >
            {primaryAction.loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
                {primaryAction.text}
              </>
            ) : (
              <>
                <i
                  className={`${primaryAction.icon} me-1`}
                  aria-hidden="true"
                />
                {primaryAction.text}
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );

  if (typeof document === "undefined") {
    return ui;
  }

  return createPortal(ui, document.body);
};

export default AiAssistantOffcanvas;
